import React, { useEffect, useState } from "react";
import { listContracts, getContract, invoiceFromMilestone } from "@/services/contracts.service";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const FromMilestoneModal = ({ open, onClose, onCreated }) => {
  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState("");
  const [milestones, setMilestones] = useState([]);
  const [milestoneId, setMilestoneId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const cs = await listContracts();
        setContracts(cs.filter((c) => c.is_payee));
      } catch {}
    })();
  }, [open]);

  useEffect(() => {
    if (!contractId) { setMilestones([]); setMilestoneId(""); return; }
    (async () => {
      try {
        const c = await getContract(contractId);
        const eligible = c.milestones.filter(
          (m) => ["Submitted", "Approved"].includes(m.status) && !m.invoice,
        );
        setMilestones(eligible);
        setMilestoneId(eligible.length === 1 ? eligible[0].milestone_id : "");
      } catch {}
    })();
  }, [contractId]);

  const selected = milestones.find((m) => m.milestone_id === milestoneId);

  const submit = async () => {
    if (!milestoneId) { toast.error("Select a milestone"); return; }
    setSaving(true);
    try {
      await invoiceFromMilestone(milestoneId);
      toast.success("Invoice generated and sent");
      setContractId(""); setMilestoneId("");
      onCreated && onCreated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="from-milestone-modal">
        <DialogHeader><DialogTitle>Create invoice from a milestone</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Pre-fills the invoice amount + title from an in-progress milestone on a contract you're delivering.</p>
          <div>
            <Label>Contract</Label>
            <select value={contractId} onChange={(e) => setContractId(e.target.value)} className="mt-1.5 h-11 w-full rounded-md border border-input bg-background px-3 text-sm accent-primary transition-colors focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring" data-testid="from-ms-contract-select">
              <option value="">Select a contract you're getting paid on…</option>
              {contracts.map((c) => (
                <option key={c.contract_id} value={c.contract_id}>{c.title}</option>
              ))}
            </select>
          </div>
          {contractId && (
            <div>
              <Label>Milestone</Label>
              <select value={milestoneId} onChange={(e) => setMilestoneId(e.target.value)} className="mt-1.5 h-11 w-full rounded-md border border-input bg-background px-3 text-sm accent-primary transition-colors focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring" data-testid="from-ms-milestone-select">
                <option value="">Select a milestone…</option>
                {milestones.map((m) => (
                  <option key={m.milestone_id} value={m.milestone_id}>{m.title} · {m.status} · ${m.amount}</option>
                ))}
              </select>
              {milestones.length === 0 && (
                <p className="mt-1 text-xs text-muted-foreground">No invoiceable milestones on this contract (work must be submitted or approved, and not already invoiced).</p>
              )}
              {selected && (
                <p className="mt-1.5 text-xs text-muted-foreground" data-testid="from-ms-selected-summary">
                  Invoicing <span className="font-medium text-foreground">{selected.title}</span> for{" "}
                  <span className="font-medium text-foreground">${selected.amount}</span>
                </p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving || !milestoneId} data-testid="from-ms-submit-btn">{saving ? "Generating…" : "Generate invoice"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FromMilestoneModal;
